import { useState } from 'react';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { Input } from 'antd';
import {
  PlusSquareOutlined,
  MinusSquareOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Button } from '../../../components';
import { useMutation } from '@apollo/client';
import Loading from '../../../components/generic/Loading';
import Sucess from '../../../components/generic/sucess';
import {
  ADD_COMMENT_FOR_SUPPLIER,
  UPDATE_DIVISION_LEVEL,
  UPDATE_PERCENTAGE_PRODUCT,
  UPDATE_PERCENTAGE_PRODUCT_GROUP,
  UPDATE_INTERMEDIATE,
} from '../queries/SupplierDependencies';
import { startCase } from 'lodash';

interface CommentsFormProps {
  onCancel: () => void;
  data?: any;
  isEditing?: boolean;
}

const mutationEndpoints = [
  UPDATE_DIVISION_LEVEL,
  UPDATE_PERCENTAGE_PRODUCT_GROUP,
  UPDATE_PERCENTAGE_PRODUCT,
  UPDATE_INTERMEDIATE,
];

const CommentsForm = ({
  data,
  onCancel,
  isEditing = false,
}: CommentsFormProps) => {
  const [editingFields, seteditingFields] = useState(
    data?.suppliers?.map((item: any) => false),
  );
  const { register, control, handleSubmit, getValues } = useForm({
    defaultValues: data,
  });

  const [sucess, setSucess] = useState(false);
  const mutation = isEditing
    ? mutationEndpoints[data?.type]
    : ADD_COMMENT_FOR_SUPPLIER;

  const [update, { loading, error }] = useMutation(mutation, {
    refetchQueries: ['FindSupplierDependencies'],
  });
  const { TextArea } = Input;

  if (error) return <>Error! {error.message}</>;

  const enableEdit = (index: number) => {
    seteditingFields(
      editingFields.map((item: any, i: number) => (i === index ? !item : item)),
    );
  };

  const findChangedSuppliers = (previousSuppliers: any, newSuppliers: any) => {
    const changedAreas: any = [];

    newSuppliers.forEach((newSupplier: any) => {
      const matchingPrevArea = previousSuppliers.find(
        (prevArea: any) =>
          prevArea?.supplierDependencyUUID ===
          newSupplier?.supplierDependencyUUID,
      );
      if (
        matchingPrevArea &&
        matchingPrevArea.percentage !== newSupplier.percentage
      ) {
        changedAreas.push({
          uuid: newSupplier?.supplierDependencyUUID,
          percentage: newSupplier?.percentage,
        });
      }
    });

    return changedAreas;
  };

  const upsertProcessArea = async (values: any) => {
    const previousSuppliers = data.suppliers;
    const newSuppliers = values?.suppliers?.map(
      ({
        percentage,
        supplierDependencyUUID,
      }: {
        percentage: string;
        supplierDependencyUUID: string;
      }) => ({
        percentage: parseFloat(percentage),
        supplierDependencyUUID: supplierDependencyUUID,
      }),
    );

    const suppliersNewData = findChangedSuppliers(
      previousSuppliers,
      newSuppliers,
    );

    if (suppliersNewData.length === 0) {
      onCancel();
      return;
    }

    try {
      suppliersNewData.forEach(async (data: any) => {
        await update({ variables: { input: data } });
      });
    } catch (err) {
      console.log(err);
    }
    onCancel();
  };

  const createComment = async (index: number) => {
    const values = getValues();

    const ObjectToSend = {
      uuid: values?.suppliers[index]?.supplierDependencyUUID,
      comment: values?.suppliers[index]?.comment,
    };

    try {
      await update({ variables: { input: ObjectToSend } });
    } catch (error) {
      console.log(error);
    }

    setSucess(true);
    setTimeout(() => {
      setSucess(false);
      enableEdit(index);
    }, 2000);
  };

  const onFinish: SubmitHandler<any> = (values) => {
    if (isEditing) {
      upsertProcessArea(values);
    } else {
      onCancel();
    }
  };

  if (loading) return <Loading />;

  return (
    <form
      className="  w-full flex flex-col flex-nowrap mt-2"
      onSubmit={handleSubmit(onFinish)}
    >
      {isEditing && (
        <h1 className="  font-bold text-lg tracking-widest">{data?.name}</h1>
      )}
      <div className="inline-flex flex-col flex-nowrap items-center my-2 justify-between gap-3">
        {data?.suppliers?.map((item: any, index: any) => (
          <>
            {editingFields[index] && loading ? (
              <Loading />
            ) : sucess && editingFields[index] ? (
              <Sucess />
            ) : (
              <section
                key={`section-${item?.uuid}`}
                className={`flex w-full justify-between items-start `}
              >
                <section className="flex items-center h-12 w-1/6">
                  <label
                    className="font-semibold text-primary-200 text-sm"
                    htmlFor={`${item?.uuid}`}
                  >
                    {startCase(item.supplierName.toLowerCase())}
                  </label>
                </section>
                {isEditing && (
                  <span className="mx-4 w-1/4 border rounded-lg text-right text-primary-200 font-normal text-sm px-2 py-1 h-auto">
                    <input
                      type="number"
                      step=".01"
                      min={0}
                      defaultValue={item.percentage}
                      className="w-24 bg-transparent border-none text-right text-primary-200 font-normal text-sm focus:outline-none focus:ring-0"
                      id={'input' + item?.uuid}
                      {...register(`suppliers.${index}.percentage`, {
                        required: isEditing,
                        min: {
                          value: 0,
                          message: 'Percentage must be greater than 0.',
                        },
                      })}
                    />
                    %
                  </span>
                )}
                <section
                  className={`relative ${isEditing ? 'w-7/12' : 'w-3/4'} `}
                >
                  <Controller
                    control={control}
                    defaultValue={item?.comment}
                    name={`suppliers.${index}.comment`}
                    render={({ field }) => (
                      <TextArea
                        autoSize={{
                          minRows: !editingFields[index] ? 2 : 4,
                          maxRows: !editingFields[index] ? 2 : 6,
                        }}
                        readOnly={isEditing || !editingFields[index]}
                        className={`rounded-lg pr-8 w-full `}
                        id={`comment-${item?.supplierDependencyUUID}`}
                        {...field}
                      />
                    )}
                  />
                  <div
                    className={`bg-white absolute ${
                      editingFields[index]
                        ? ' top-[2px]'
                        : 'top-1/2 -translate-y-1/2'
                    } transform  right-3`}
                  >
                    {!editingFields[index] ? (
                      !isEditing ? (
                        <EditOutlined
                          className="text-blue hover:cursor-pointer"
                          onClick={() => enableEdit(index)}
                        />
                      ) : (
                        <PlusSquareOutlined
                          className="text-blue hover:cursor-pointer"
                          onClick={() => enableEdit(index)}
                        />
                      )
                    ) : !isEditing ? (
                      <SaveOutlined
                        className="text-blue hover:cursor-pointer"
                        onClick={() => createComment(index)}
                      />
                    ) : (
                      <MinusSquareOutlined
                        className="text-blue hover:cursor-pointer"
                        onClick={() => enableEdit(index)}
                      />
                    )}
                  </div>
                </section>
              </section>
            )}
          </>
        ))}
      </div>
    </form>
  );
};

export default CommentsForm;
